import { type QuizQuestionHidden } from '@efdigital/ll-addon-data';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { uniqueId } from '@ember/helper';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import { type QuizQuestionEditorComponentSignature } from '../../types/question-manager.ts';

const WORKFLOW_VALUES: Record<string, string> = {
  '$$user.name': 'User/participant name',
  '$$user.email': 'User/participant email',
  '$$user.id': 'User/participant user ID',
  '$$invitation.name': 'Reviewer name (assessments only)',
  '$$invitation.email': 'Reviewer email (assessments only)',
};

const WORKFLOW_OPTIONS: { key: string; label: string }[] = Object.entries(
  WORKFLOW_VALUES
).map(([key, label]) => ({ key, label }));

function isWorkflowValue(value: string): boolean {
  return !!value && !!WORKFLOW_VALUES[value];
}

export class HiddenQuestionEditorComponent extends Component<QuizQuestionEditorComponentSignature> {
  get questionData(): QuizQuestionHidden {
    return this.args.question.hidden!;
  }

  setShowValue = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.questionData.showValue = target.checked;
  };

  setQuery = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.questionData.query = target.value;
  };

  setDefaultValue = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.questionData.defaultValue = target.value;
  };

  get valueSourceIsWorkflow(): boolean {
    if (this.temporaryValueSource === 'workflow') {
      return true;
    }

    return isWorkflowValue(this.questionData.query);
  }

  get valueSourceIsQueryString(): boolean {
    if (this.temporaryValueSource === 'query') {
      return true;
    }
    return !!this.questionData.query && !this.valueSourceIsWorkflow;
  }

  get valueSourceIsHardcoded(): boolean {
    return !this.valueSourceIsWorkflow && !this.valueSourceIsQueryString;
  }

  @tracked temporaryValueSource: 'query' | 'workflow' | null = null;

  setValueSource = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const source = target.value;

    console.log('setValueSource', source);
    console.log('current query', this.questionData.query);
    console.log('current default', this.questionData.defaultValue);

    switch (source) {
      case 'query':
        if (isWorkflowValue(this.questionData.query)) {
          this.questionData.query = '';
        }
        this.temporaryValueSource = 'query';
        break;

      case 'workflow':
        if (!isWorkflowValue(this.questionData.query)) {
          this.questionData.query = '';
        }
        this.temporaryValueSource = 'workflow';
        break;

      case 'hardcoded':
        this.questionData.query = '';
        this.temporaryValueSource = null;
        break;

      default:
        console.warn('Unknown value source', source);
        break;
    }
  };

  <template>
    {{#let (uniqueId) as |uniqueId|}}
      <div class="card-body">
        <div class="form-text mb-4">Store values silently (e.g. user IDs,
          referral codes) for use in workflows or reporting.</div>

        <div class="mb-4">
          <label class="form-label">How should the value be populated?</label>

          <div class="form-check">
            <input
              type="radio"
              class="form-check-input"
              name="valueSource"
              id="{{uniqueId}}-valueSource-workflow"
              value="workflow"
              checked={{this.valueSourceIsWorkflow}}
              {{on "change" this.setValueSource}}
            />
            <label
              for="{{uniqueId}}-valueSource-workflow"
              class="form-check-label"
            >By a workflow</label>
          </div>

          <div class="form-check">
            <input
              type="radio"
              class="form-check-input"
              name="valueSource"
              id="{{uniqueId}}-valueSource-query"
              value="query"
              checked={{this.valueSourceIsQueryString}}
              {{on "change" this.setValueSource}}
            />
            <label
              for="{{uniqueId}}-valueSource-query"
              class="form-check-label"
            >By a query string parameter</label>
          </div>

          <div class="form-check">
            <input
              type="radio"
              class="form-check-input"
              name="valueSource"
              id="{{uniqueId}}-valueSource-hardcoded"
              value="hardcoded"
              checked={{this.valueSourceIsHardcoded}}
              {{on "change" this.setValueSource}}
            />
            <label
              for="{{uniqueId}}-valueSource-hardcoded"
              class="form-check-label"
            >Constant value</label>
          </div>
        </div>

        {{#if this.valueSourceIsQueryString}}
          <div class="mb-4">
            <label class="form-label">Query String Parameter</label>
            <input
              type="text"
              class="form-control"
              value={{this.questionData.query}}
              id="{{uniqueId}}-query"
              {{on "change" this.setQuery}}
            />
            <div class="form-text">The value will be populated from the query
              string parameter with this name. This is useful for tracking when
              the form is shared via a link.</div>
            <div class="form-text">The default value will be used if no value is
              found in the query string.</div>
          </div>
        {{/if}}

        {{#if this.valueSourceIsWorkflow}}
          <div class="mb-4">
            <label class="form-label">Workflow Parameter</label>
            <select
              class="form-select"
              id="{{uniqueId}}-workflow"
              {{on "change" this.setQuery}}
              {{selectValue this.questionData.query}}
            >
              <option value="">Please select...</option>
              {{#each WORKFLOW_OPTIONS as |workflow|}}
                <option value={{workflow.key}}>{{workflow.label}}</option>
              {{/each}}
            </select>
            <div class="form-text">The value will be populated if it is managed
              by a workflow. If the workflow does not set a value then the
              default value will be used.</div>
          </div>
        {{/if}}

        <div class="mb-4">
          <label class="form-label">{{#if
              this.valueSourceIsHardcoded
            }}Value{{else}}Default Value{{/if}}</label>
          <input
            type="text"
            class="form-control"
            value={{this.questionData.defaultValue}}
            id="{{uniqueId}}-defaultValue"
            {{on "change" this.setDefaultValue}}
          />
        </div>

        <hr />

        <div class="form-check">
          <input
            type="checkbox"
            class="form-check-input"
            checked={{this.questionData.showValue}}
            id="{{uniqueId}}-showValue"
            {{on "change" this.setShowValue}}
          />
          <label for="{{uniqueId}}-showValue" class="form-check-label">Show
            value to end user</label>
        </div>
      </div>
    {{/let}}
  </template>
}
